import React, {useCallback} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Time} from 'yougile-util'
import {getGTDLastChangeTime, getGTDStage, Hub, Subtask} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {IconButtonNew} from '@components/common/base/buttons'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {MessageBox} from '@components/message-box'
import {useSyncEventUpdateEffect} from '@containers/hooks'
import {Task} from '@extensions/task'

export const TwIconButtonNew = tw(IconButtonNew)<{$isTaskColored?: boolean}>`
  ${p =>
    p.$isTaskColored
      ? '!text-icon-primary hover:!text-icon-primary active:!text-icon-primary opacity-[.64]'
      : '!text-secondary hover:!text-secondary active:!text-secondary'}
`

export interface DoneButtonProps {
  hub: Hub
  fromWhere?: 'my-tasks' | 'board' | 'right-panel' | 'timeline'
  className?: string
  isParentTaskTemplate?: boolean
  placeholder?: Subtask | undefined
  isTaskColored?: boolean
  isStatic?: boolean
}

const getTooltip = (
  isDone: boolean,
  doneAt?: number,
  isParentTaskTemplate?: boolean
) => {
  if (isParentTaskTemplate) {
    return Lang.msg.taskTemplateNotCompleted
  }

  return isDone
    ? Lang.msg.markTaskUncompleted(Time.formatDateTime(doneAt))
    : Lang.msg.markTaskCompleted
}

export const DoneButton = (props: DoneButtonProps) => {
  const {
    hub,
    fromWhere,
    className,
    isParentTaskTemplate,
    isTaskColored,
    isStatic
  } = props
  const {placeholder} = props

  useSyncEventUpdateEffect('current', {
    filter: hub.id,
    debounceTime: 50,
    deps: [hub.id]
  })

  const stage = getGTDStage(hub.data.gtd)
  const doneAt = getGTDLastChangeTime(hub.data.gtd)
  const isDone = stage === 'done'
  let iconValue: boolean

  if (placeholder) {
    iconValue = placeholder.isCompleted
  } else {
    iconValue = isDone
  }

  const icon = iconValue
    ? 'IconCustomTaskDoneFilled'
    : 'IconCustomTaskDoneEmpty'
  const tooltip = getTooltip(isDone, doneAt, isParentTaskTemplate)

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()

      if (isStatic) {
        return
      }

      if (isParentTaskTemplate) {
        return
      }

      if (!PermissionsChecker.checkComplete(hub)) {
        MessageBox.warn(Lang.msg.permissionsFobiddenTaskComplete)
        return
      }
      if (
        PermissionsChecker.shouldFallbackToOldCheckComplete(hub) &&
        !PermissionsChecker.oldCheckComplete(hub)
      ) {
        MessageBox.warn(Lang.msg.permissionsFobiddenTaskComplete)
        return
      }

      const newValue = !isDone

      Task.changeCompleted(hub, newValue, fromWhere)
    },
    [hub.id, isDone, fromWhere, isStatic]
  )

  return (
    <Tooltip content={tooltip} disabled={isStatic}>
      <TwIconButtonNew
        icon={icon}
        onClick={onClick}
        className={className}
        $isTaskColored={isTaskColored}
        disabled={isStatic}
      />
    </Tooltip>
  )
}
